import React, { useEffect, useState } from 'react';
import ContactListRowItem from './ContactListRowItem';
import { TAppContact } from 'TProtocol/common/models';
import { ScrollableContent } from './styled';
import { IUDBKeyedContact } from '../lib/ContactsDatabase';
import { useContactsContext } from '../contexts/ContactsContext';

export interface VirtualScrollerSettings {
  amount: number;
  itemHeight: number;
  minIndex: number;
  maxIndex: number;
  startIndex: number;
  tolerance: number;
  data: IUDBKeyedContact[];
}

interface Props {
  settings: VirtualScrollerSettings;
}

interface State {
  topPaddingHeight: number;
  bottomPaddingHeight: number;
  data: TAppContact[];
  currentIndex: number;
}

const VirtualScroller = ({ settings }: Props) => {
  const contactsContext = useContactsContext();
  const maxIndex = settings.maxIndex;
  const minIndex = settings.minIndex;
  const itemHeight = settings.itemHeight;
  const totalHeight = (maxIndex - minIndex + 1) * itemHeight;
  const toleranceHeight = settings.tolerance * itemHeight;
  const bufferedItems = settings.amount + 2 * settings.tolerance;
  const itemsAbove = settings.startIndex - settings.tolerance - minIndex;

  const [state, setState] = useState<State>({
    topPaddingHeight: itemsAbove * itemHeight,
    bottomPaddingHeight: totalHeight - (itemsAbove * itemHeight),
    data: [],
    currentIndex: 0,
  });

  const initialPosition = (itemsAbove * itemHeight) + toleranceHeight;

  const viewportRef = React.createRef<HTMLDivElement>();


  useEffect(() => {
    const position = state.currentIndex === 0 ? initialPosition : (state.currentIndex * itemHeight) + toleranceHeight;
    void runScroller(position, true);
  }, [settings.data, contactsContext.clientModificationId]);

  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    void runScroller(e.currentTarget.scrollTop);
  };

  const runScroller = async (scrollTop: number, forceLoad = false) => {
    const index = minIndex + Math.floor((scrollTop - toleranceHeight) / itemHeight);

    // Only reload if we moved at least two rows' worth of distance, because there is a bug where it will bounce
    // between two adjacent indices if the position is right between them.
    if (forceLoad || state.currentIndex === undefined || Math.abs(index - state.currentIndex) > 1) {
      const data = calculateData(index, bufferedItems);
      const topPaddingHeight = Math.max((index - minIndex) * itemHeight, 0);
      const bottomPaddingHeight = Math.max(
        totalHeight - topPaddingHeight - data.length * itemHeight,
        0
      );

      const convertedData: TAppContact[] = [];
      const contactsMap = new Map<string, TAppContact>();
      for (const datum of data) {
        let contact = contactsContext.visibleContactsMap.get(datum.contactId);
        if (contact === undefined) {
          contact = await contactsContext.getContact(datum.contactId);
        }
        if (contact !== undefined) {
          contactsMap.set(datum.contactId, contact);
        }
        if (contact) {
          convertedData.push(contact);
        }
      }

      setState({
        topPaddingHeight,
        bottomPaddingHeight,
        data: convertedData,
        currentIndex: index
      });
      contactsContext.setVisibleContactsMap(contactsMap);
    }
  };

  const calculateData = (offset: number, limit: number) => {
    const data = [];
    const start = Math.max(minIndex, offset);
    const end = Math.min(offset + limit - 1, maxIndex);
    if (start <= end) {
      for (let i = start; i <= end; i++) {
        if (settings.data[i]) {
          data.push(settings.data[i]);
        }
      }
    }
    return data;
  };

  return (<>
      <ScrollableContent
        ref={viewportRef}
        onScroll={onScroll}
      >
        <div style={{ height: state.topPaddingHeight }}/>
        {state.data.map((contact) => {
          return <ContactListRowItem contact={contact} key={contact.contactId}/>;
        })}
        <div style={{ height: state.bottomPaddingHeight }}/>
      </ScrollableContent>
    </>
  );

};

export default VirtualScroller;
